import {
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
} from "@material-ui/core";
import Box from "@material-ui/core/Box";
import Button from "@material-ui/core/Button";
import Dialog from "@material-ui/core/Dialog";
import MUITextField from "@material-ui/core/TextField";
import Icon from "@material-ui/icons/BugReport";
import { formatDisplayDateTime } from "../utils/date";
import { capitalize } from "lodash";
import React, { FC, Fragment } from "react";
import {
  ArrayField,
  ArrayInput,
  AutocompleteInput,
  Create,
  CreateProps,
  Datagrid,
  DateInput,
  Edit,
  EditProps,
  Filter,
  FormDataConsumer,
  FunctionField,
  List,
  ListProps,
  NumberInput,
  Record,
  ReferenceField,
  ReferenceInput,
  SaveButton,
  SelectInput,
  Show,
  ShowProps,
  SimpleForm,
  SimpleFormIterator,
  SimpleShowLayout,
  TextField,
  TextInput,
  Toolbar,
  useDataProvider,
  useListContext,
  useNotify,
  useRefresh,
  useUnselectAll,
} from "react-admin";
import CrudBreadcrumb from "../components/CrudBreadcrumb";
import StatusField from "../components/StatusField";
import TitleBreadcrumb from "../components/TitleBreadcrumb";
import { BookingIdField, LocalDateField } from "../components/fields";
import ProductInstanceField from "../components/fields/ProductInstanceIdField";
import { foundIn } from "../utils";
import ReporterInput from "../components/inputs/ReporterInput";

const resourceName = "issues";
const maxChildCodeSelection = 10;

interface Props {
  selectedIds?: number[];
  onComplete?: () => void;
}

const ResolveMultipleIssueDialog: React.FC<Props> = ({
  selectedIds,
  onComplete,
}) => {
  const dataProvider = useDataProvider();
  const notify = useNotify();

  const [open, setOpen] = React.useState(false);
  const [isLoading, setIsLoading] = React.useState(false);
  const [closingRemarks, setClosingRemarks] = React.useState("");
  const [resolvableIssues, setResolvableIssues] = React.useState<any[]>([]);

  const hasSelection = Boolean(selectedIds && selectedIds.length > 0);

  const handleOpenDialog = () => {
    if (!hasSelection) {
      notify("Please select issues first", "warning");
      return;
    }

    setResolvableIssues(selectedIds!.map((id) => ({ id })));
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
    setClosingRemarks("");
  };

  const handleAccept = async () => {
    setIsLoading(true);

    try {
      const updatePromises = resolvableIssues.map((issue) =>
        dataProvider.update("issues", {
          id: issue.id,
          data: {
            status: IssueStatus.RESOLVED,
            closing_remarks: closingRemarks,
          },
          previousData: issue,
        }),
      );

      await Promise.all(updatePromises);

      notify(
        `Successfully resolved ${resolvableIssues.length} issues.`,
        "info",
      );

      setOpen(false);
      setClosingRemarks("");
      onComplete?.();
    } catch (error: any) {
      notify(`Error: ${error.message}`, "warning");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      <Button
        color="primary"
        onClick={handleOpenDialog}
        size="small"
        disabled={!hasSelection}
      >
        Resolve Issues {hasSelection ? `(${selectedIds!.length})` : ""}
      </Button>

      <Dialog
        open={open}
        onClose={handleClose}
        aria-labelledby="resolve-multiple-issues-title"
      >
        <DialogTitle id="resolve-multiple-issues-title">
          Resolve Issues
        </DialogTitle>

        <DialogContent>
          <DialogContentText>
            You have <strong>{selectedIds?.length || 0} issues</strong>{" "}
            selected.
            <br />
            Please confirm resolving these issues.
          </DialogContentText>

          <MUITextField
            fullWidth
            multiline
            rows={3}
            label="Closing Remarks (optional)"
            value={closingRemarks}
            onChange={(e) => setClosingRemarks(e.target.value)}
            margin="dense"
          />
        </DialogContent>

        <DialogActions>
          <Button onClick={handleClose} color="secondary">
            Cancel
          </Button>
          <Button onClick={handleAccept} color="primary" disabled={isLoading}>
            {isLoading
              ? "Resolving..."
              : `Resolve ${selectedIds?.length || 0} Issues`}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

interface IssueBulkActionsProps {
  selectedIds?: number[];
  data?: any[];
}

export const IssueBulkActions: React.FC<IssueBulkActionsProps> = ({
  selectedIds,
}) => {
  const refresh = useRefresh();
  const unselectAll = useUnselectAll("issues");
  const listContext = useListContext();

  const statusFilter = listContext?.filterValues?.status;
  const hasSelection = Boolean(selectedIds && selectedIds.length > 0);

  const handleActionComplete = () => {
    refresh();
    unselectAll();
  };

  const canBulkResolve =
    hasSelection && (!statusFilter || statusFilter === IssueStatus.OPEN);

  return (
    <Fragment>
      {canBulkResolve && (
        <ResolveMultipleIssueDialog
          selectedIds={selectedIds || []}
          onComplete={handleActionComplete}
        />
      )}
    </Fragment>
  );
};

const ResourceList: FC<ListProps> = (props) => {
  const isRowSelectable = (record: Record): boolean => {
    // Allow selection for deliveries that can be assigned, started, finished, or verified
    return foundIn(record.status, [IssueStatus.OPEN]);
  };

  return (
    <List
      {...props}
      exporter={false}
      filters={<ListFilter />}
      bulkActionButtons={<IssueBulkActions />}
    >
      <Datagrid rowClick="show" isRowSelectable={isRowSelectable}>
        <TextField source="id" />
        <LocalDateField source="created_at" showTime={true} />
        <BookingIdField source="booking" />
        <ProductInstanceField source="product_instance" />
        <FunctionField
          label="Total Child Code"
          render={(record: any) => {
            const codes: any[] = record.child_codes ?? [];
            if (codes.length === 0) return 0;
            return (
              <Box>
                {codes.map((item: any, idx: number) => (
                  <div key={idx}>
                    {item?.child_code?.product_code ??
                      item?.child_code_id ??
                      "-"}{" "}
                    x {item?.quantity ?? 1}
                  </div>
                ))}
              </Box>
            );
          }}
        />

        <ReferenceField
          source="reporter_id"
          reference="users"
          label="Reported By"
        >
          <FunctionField
            render={(record: any) =>
              record?.admin?.full_name ??
              record?.consultant?.full_name ??
              record?.driver?.full_name ??
              record?.email ??
              "-"
            }
          />
        </ReferenceField>
        <TextField source="description" />
        <LocalDateField source="resolved_at" label="Resolved At" showTime />
        <FunctionField
          label="Resolved By"
          render={(record: any) =>
            record?.resolved_by?.admin?.full_name ??
            record?.resolved_by?.driver?.full_name ??
            record?.resolved_by?.consultant?.full_name ??
            "-"
          }
        />
        <StatusField source="status" />
      </Datagrid>
    </List>
  );
};

const ListFilter: FC<any> = (props) => (
  <Filter {...props}>
    <TextInput source="id" label="ID" />
    <DateInput source="date" />
    <SelectInput
      source="status"
      allowEmpty
      emptyText="All"
      choices={[
        { id: IssueStatus.OPEN, name: "Open" },
        { id: IssueStatus.RESOLVED, name: "Resolved" },
      ]}
    />
    <ReferenceInput
      label="Set"
      source="product_instance_id"
      reference="product-instances"
      allowEmpty
    >
      <AutocompleteInput optionText="name" />
    </ReferenceInput>
    <ReporterInput
      label="Report by"
      source="reporter_id"
    />
  </Filter>
);

const ResourceShow: FC<ShowProps> = (props) => {
  const breadcrumbPaths = [
    { link: `/${resourceName}`, title: `${capitalize(resourceName)}` },
    { link: `/${resourceName}/${props.id}/show`, title: `${props.id}` },
  ];

  return (
    <>
      <TitleBreadcrumb paths={breadcrumbPaths} />
      <Show {...props}>
        <SimpleShowLayout>
          <TextField source="id" />
          <LocalDateField source="updated_at" showTime />
          <BookingIdField source="booking" />
          <ProductInstanceField source="product_instance" />
          <ReferenceField
            source="reporter_id"
            reference="users"
            label="Reported By"
          >
            <FunctionField
              render={(record: any) =>
                record?.admin?.full_name ??
                record?.consultant?.full_name ??
                record?.driver?.full_name ??
                record?.email ??
                "-"
              }
            />
          </ReferenceField>
          <TextField source="status" />
          <TextField source="description" />
          <ArrayField source="child_codes" label="Child Codes">
            <Datagrid>
              <FunctionField
                label="Child Code"
                render={(record: any) =>
                  record?.child_code?.product_code ?? record?.child_code_id ?? "-"
                }
              />
              <TextField source="quantity" label="Qty" />
              <TextField source="note" label="Note" />
            </Datagrid>
          </ArrayField>
          <TextField source="warehouse_note" />
          <TextField source="closing_remarks" />
          <LocalDateField source="resolved_at" label="Resolved At" showTime />
          <FunctionField
            label="Resolved By"
            render={(record: any) =>
              record?.resolved_by?.admin?.full_name ??
              record?.resolved_by?.driver?.full_name ??
              record?.resolved_by?.consultant?.full_name ??
              "-"
            }
          />
        </SimpleShowLayout>
      </Show>
    </>
  );
};

const IssueEditToolbar: FC<any> = (props) => (
  <Toolbar {...props}>
    <SaveButton disabled={props.pristine} />
  </Toolbar>
);

const sanitizeChildCodesPayload = (childCodeItems: any[] = []) =>
  childCodeItems
    .filter((item) => item?.child_code_id)
    .map((item) => ({
      child_code_id: item.child_code_id,
      note: item.note || "",
      quantity: Number(item.quantity) || 1,
    }));

const ChildCodeItemsInput: FC = () => (
  <FormDataConsumer>
    {({ formData }) => {
      const childCodeItems = Array.isArray(formData?.child_codes)
        ? formData.child_codes
        : [];
      const reachedLimit = childCodeItems.length >= maxChildCodeSelection;

      return (
        <>
          <ArrayInput source="child_codes" label={`Child Codes (max ${maxChildCodeSelection})`}>
            <SimpleFormIterator disableReordering disableAdd={reachedLimit}>
              <ReferenceInput
                source="child_code_id"
                reference="child-codes"
                label="Child Code"
                perPage={100}
                sort={{ field: "product_code", order: "ASC" }}
              >
                <AutocompleteInput
                  optionText={(record: any) =>
                    record?.product_code
                      ? `${record.product_code} - ${record.description ?? ""}`
                      : ""
                  }
                  fullWidth
                />
              </ReferenceInput>
              <NumberInput source="quantity" label="Qty" min={1} />
              <TextInput source="note" label="Note" />
            </SimpleFormIterator>
          </ArrayInput>
          {reachedLimit ? (
            <span style={{ color: "#f44336", fontSize: 12 }}>
              Maximum {maxChildCodeSelection} child codes reached.
            </span>
          ) : null}
        </>
      );
    }}
  </FormDataConsumer>
);

const ResourceEdit: FC<EditProps> = (props) => (
  <Edit {...props}>
    <SimpleForm toolbar={<IssueEditToolbar />}>
      <TextField source="id" />
      <LocalDateField source="updated_at" showTime />
      <ReferenceInput source="booking_id" reference="bookings">
        <AutocompleteInput
          optionText={(record) => {
            if (!record) return "";

            const createdAt = record.date
              ? formatDisplayDateTime(record.date)
              : "-";

            const surgeonName = record.surgeon?.full_name ?? "-";

            return `${record.id ?? "-"} - ${createdAt} - ${surgeonName}`;
          }}
        />
      </ReferenceInput>
      <ReferenceInput
        label="Product Instance"
        source="product_instance_id"
        reference="product-instances"
        sort={{ field: "name", order: "asc" }}
        perPage={1000}
      >
        <AutocompleteInput optionText="name" />
      </ReferenceInput>
      <ReporterInput source="reporter_id" label="Reported By" />
      <SelectInput
        source="status"
        choices={[
          { id: IssueStatus.OPEN, name: "Open" },
          { id: IssueStatus.RESOLVED, name: "Resolved" },
        ]}
      />
      <TextInput source="description" />
      <ChildCodeItemsInput />
      <TextInput source="warehouse_note" />
      <FormDataConsumer>
        {({ formData, ...rest }) =>
          formData.status === IssueStatus.RESOLVED && (
            <TextInput source="closing_remarks" />
          )
        }
      </FormDataConsumer>
    </SimpleForm>
  </Edit>
);

const ResourceCreate: FC<CreateProps> = (props) => {
  return (
    <>
      <CrudBreadcrumb
        resourceName={Common.resourceName}
        resourceId={""}
        mode="create"
      />
      <Create {...props}>
        <SimpleForm
          toolbar={
            <Toolbar>
              <SaveButton
                transform={(data) => {
                  const { child_codes, ...rest } = data;

                  return {
                    ...rest,
                    child_codes: sanitizeChildCodesPayload(child_codes),
                  };
                }}
              />
            </Toolbar>
          }
        >
          <ReferenceInput source="booking_id" reference="bookings">
            <AutocompleteInput
              optionText={(record) => {
                if (!record) return "";

                const createdAt = record.date
                  ? formatDisplayDateTime(record.date)
                  : "-";

                const surgeonName = record.surgeon?.full_name ?? "-";

                return `${record.id ?? "-"} - ${createdAt} - ${surgeonName}`;
              }}
            />
          </ReferenceInput>
          <ReferenceInput
            label="Product Instance"
            source="product_instance_id"
            reference="product-instances"
            sort={{ field: "name", order: "ASC" }}
            perPage={1000}
          >
            <AutocompleteInput optionText="name" />
          </ReferenceInput>
          <ReporterInput source="reporter_id" label="Reported By" />
          <TextInput source="description" />
          <ChildCodeItemsInput />
          <TextInput source="warehouse_note" />
        </SimpleForm>
      </Create>
    </>
  );
};

const Common = {
  resourceName: "issues",
};

// eslint-disable-next-line import/no-anonymous-default-export
export default {
  name: resourceName,
  icon: Icon,
  list: ResourceList,
  show: ResourceShow,
  edit: ResourceEdit,
  create: ResourceCreate,
};

export const IssueStatus = {
  OPEN: "open",
  RESOLVED: "resolved",
};
